import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AthleteBibs } from "../../components/athletes/athlete-bibs";
import { AthleteEntries } from "../../components/athletes/athlete-entries";
import { AthleteMedals } from "../../components/athletes/athlete-medals";
import { athleteDetailQuery } from "../../components/athletes/queries";
import { formatList } from "../../components/meets-list/intl";
import { DetailFailure, DetailHeader, DetailSkeleton, OUTLINE_LINK } from "../../components/nocs/detail-parts";
import { idParam } from "../../components/nocs/list-params";
import { MedalCounts } from "../../components/nocs/medal-counts";
import { NocBadge } from "../../components/nocs/noc-badge";
import { useLinkedGames } from "../../components/nocs/use-linked-games";
import { ShellCrumbs } from "../../components/shell/crumbs";
import { formatInteger } from "../../lib/i18n/format";

/** `?games=` — the games the link was made in (a copied link opens the same games). */
const athleteSearch = z.object({ games: idParam });
const isUuid = (v: string) => z.string().uuid().safeParse(v).success;

/**
 * One athlete in one games (`GET /athletes/:personId`): who they compete
 * for, their entries and the medals the desk awarded. Only what the API
 * returns — a printed name, a folded gender, the organization, the events;
 * nothing else about the person.
 */
export const Route = createFileRoute("/_app/athletes/$personId")({
	validateSearch: athleteSearch,
	component: AthletePage,
});

function AthletePage() {
	const { t } = useTranslation("athletes");
	const { personId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/athletes/$personId" });
	const games = useLinkedGames(search.games, (id) => void navigate({ search: { games: id }, replace: true }));
	const valid = isUuid(personId);

	const q = useQuery({ ...athleteDetailQuery(games.gamesId ?? "", personId.toLowerCase()), enabled: games.gamesId !== null && valid });
	const athlete = q.data;
	const gamesName = games.games?.name ?? "";

	const crumbs = (
		<ShellCrumbs
			items={[{ label: t("title"), to: { to: "/athletes" } }, { label: athlete?.name ?? t("detailTitle") }]}
		/>
	);

	if (!valid || q.isError) {
		return (
			<div className="flex flex-col gap-[1rem]">
				{crumbs}
				<h1 className="font-black text-[1.5rem] text-ink">{t("detailTitle")}</h1>
				<DetailFailure
					error={valid ? q.error : "not-found"}
					notFound={{
						title: t("detail.notFoundTitle", { games: gamesName }),
						body: t("detail.notFoundBody"),
						action: (
							<Link to="/athletes" className={OUTLINE_LINK}>
								{t("detail.backToList")}
							</Link>
						),
					}}
					onRetry={() => void q.refetch()}
				/>
			</div>
		);
	}

	if (!athlete) {
		return (
			<div className="flex flex-col">
				{crumbs}
				<DetailSkeleton label={t("detail.loading")} />
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			{crumbs}
			<DetailHeader
				kicker={<NocBadge noc={athlete.noc} withName />}
				title={athlete.name}
				facts={[
					{ key: "gender", label: t("detail.facts.gender"), value: athlete.gender ? t(`gender.${athlete.gender}`) : <span className="text-muted">{t("gender.unknown")}</span> },
					{ key: "sports", label: t("detail.facts.sports"), value: formatList(athlete.sports.map((s) => s.name)) || "–" },
					{ key: "entries", label: t("detail.facts.entries"), value: <span className="font-mono">{formatInteger(athlete.events_count)}</span> },
				]}
				aside={<MedalCounts medals={athlete.medals} size="lg" />}
			/>
			<div className="flex flex-col gap-[1rem]">
				{games.gamesId && <AthleteBibs gamesId={games.gamesId} personId={athlete.people_id} />}
				<AthleteEntries entries={athlete.entries} total={athlete.events_count} gamesName={gamesName} />
				<AthleteMedals medals={athlete.awarded_medals} />
			</div>
		</div>
	);
}
